import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE, getAuthHeaders } from './client';

// ==================== TYPES ====================

/**
 * Firebase backend LLM request log
 * Used for transparent logging of API requests and responses
 */
export interface LLMRequestLog {
  id: string;
  userId: string;
  jobId?: string;
  artifactType: string;
  providerId: string;
  status: 'pending_execution' | 'executing' | 'completed' | 'failed';

  // Provider configuration details
  providerConfig?: {
    name: 'anthropic' | 'openai' | 'gemini';
    displayName: string;
    modelId: string;
    config?: {
      temperature?: number;
      maxTokens?: number;
      topP?: number;
    };
  };

  // Request details
  prompt?: string;
  requestBody?: {
    jobDescription: string;
    userContent: string;
    jobTitle: string;
    company: string;
  };

  // API request payload (what gets sent to the LLM provider)
  apiRequest?: {
    model: string;
    messages?: Array<{ role: string; content: string }>;
    max_tokens?: number;
    temperature?: number;
    messages_openai?: Array<{ role: string; content: string }>;
    max_tokens_openai?: number;
    generationConfig?: {
      maxOutputTokens?: number;
      temperature?: number;
    };
  };

  // Response details
  result?: string;
  errorMessage?: string;
  tokenUsage?: {
    input: number;
    output: number;
    total: number;
  };
  model?: string;
  provider?: string;

  // Metadata
  createdAt: string;
  executedAt?: string;
  completedAt?: string;
}

export interface LlmRequest {
  id: string;
  job_id: string;
  provider_name: string;
  model_name: string;
  artifact_type: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  request_body: Record<string, any>;
  result?: Record<string, any> | null;
  error_message?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateLlmRequestRequest {
  job_id: string;
  provider_name: string;
  model_name: string;
  artifact_type: string;
  status?: string;
  request_body: Record<string, any>;
}

export interface UpdateLlmRequestRequest {
  status?: string;
  result?: Record<string, any> | null;
  error_message?: string | null;
}

// ==================== QUERIES ====================

/**
 * Fetch active/recent LLM requests for a job
 * Optionally filter by status ('queued', 'processing', 'completed', 'failed')
 */
export const useJobLlmRequests = (jobId: string | undefined, status?: string) => {
  return useQuery({
    queryKey: ['llm-requests', 'byJob', jobId, status],
    queryFn: async () => {
      const url = new URL(`${API_BASE}/llm-requests/job/${jobId}`);
      if (status) url.searchParams.append('status', status);

      const headers = await getAuthHeaders();
      const res = await fetch(url.toString(), { headers });
      if (!res.ok) throw new Error('Failed to fetch LLM requests');
      return (await res.json()) as LlmRequest[];
    },
    enabled: !!jobId,
  });
};

/**
 * Fetch a specific LLM request by ID
 */
export const useLlmRequest = (id: string | undefined) => {
  return useQuery({
    queryKey: ['llm-requests', id],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/llm-requests/${id}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch LLM request');
      return (await res.json()) as LlmRequest;
    },
    enabled: !!id,
  });
};

// ==================== MUTATIONS ====================

/**
 * Create a new LLM request log entry
 */
export const useCreateLlmRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateLlmRequestRequest) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/llm-requests`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create LLM request');
      }
      return (await res.json()) as LlmRequest;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['llm-requests'] });
      queryClient.invalidateQueries({ queryKey: ['llm-requests', 'byJob', data.job_id] });
    },
  });
};

/**
 * Update an existing LLM request (e.g., mark as completed or failed)
 */
export const useUpdateLlmRequest = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateLlmRequestRequest) => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/llm-requests/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update LLM request');
      }
      return (await res.json()) as LlmRequest;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['llm-requests'] });
      queryClient.invalidateQueries({ queryKey: ['llm-requests', id] });
      queryClient.invalidateQueries({ queryKey: ['llm-requests', 'byJob', data.job_id] });
    },
  });
};

/**
 * Delete an LLM request (mostly for cleanup)
 */
export const useDeleteLlmRequest = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/llm-requests/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to delete LLM request');
      }
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['llm-requests'] });
    },
  });
};

/**
 * Hook to check if there are any active/processing LLM requests for a job
 * Returns the first processing request or undefined
 */
export const useActiveJobLlmRequest = (jobId: string | undefined) => {
  const { data: requests = [] } = useJobLlmRequests(jobId);

  const activeRequest = requests.find(
    (req) => req.status === 'queued' || req.status === 'processing'
  );

  return activeRequest;
};

/**
 * Hook to check when an LLM request completes
 * Will stop polling once status is 'completed' or 'failed'
 */
export const useWaitForLlmRequest = (requestId: string | undefined) => {
  return useQuery({
    queryKey: ['llm-requests', 'wait', requestId],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/llm-requests/${requestId}`, { headers });
      if (!res.ok) throw new Error('Failed to fetch LLM request');
      return (await res.json()) as LlmRequest;
    },
    enabled: !!requestId,
    // Poll every 5 seconds
    refetchInterval: 5000,
    // Stop polling when request is completed or failed
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
  });
};

// ==================== FIREBASE BACKEND HOOKS ====================

/**
 * Fetch all LLM request logs for the current user (Firebase backend)
 * Returns the transparent request logs with prompts, responses, and token usage
 */
export const useGetLLMRequests = (limit: number = 50) => {
  return useQuery({
    queryKey: ['llm-requests', 'firebase', 'all'],
    queryFn: async () => {
      const url = `${API_BASE}/artifacts/llm-requests/list?limit=${limit}`;

      const headers = await getAuthHeaders();
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error('Failed to fetch LLM request logs');

      const response = await res.json();
      return response.data as LLMRequestLog[];
    },
  });
};
